import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { ProductTag } from '@/models/product';

const tagUrls = (liftOperatorId: string) => ({
    base: urlBuilder('liftoperators', `liftoperators/${liftOperatorId}/tags`),
});

type CreateTagPayload = { name: string };
type UpdateTagPayload = { name: string };

export type TagUsage = {
    count: number;
    productConfigurationIds: string[];
};

export const tagsApi = baseApiAuthenticated
    .enhanceEndpoints({
        addTagTypes: ['PRODUCT_TAGS', 'PRODUCT_CONFIGURATION'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getTags: builder.query<ProductTag[], { liftOperatorId: string }>({
                query: ({ liftOperatorId }) => tagUrls(liftOperatorId).base,
                providesTags: ['PRODUCT_TAGS'],
            }),
            createTag: builder.mutation<
                ProductTag,
                { liftOperatorId: string; payload: CreateTagPayload }
            >({
                query: ({ liftOperatorId, payload }) => ({
                    url: tagUrls(liftOperatorId).base,
                    method: 'POST',
                    body: payload,
                }),
                // Creating a tag leaves every existing product configuration untouched. Invalidating them as well
                // refetches the configuration currently open in the editor and discards unsaved edits.
                invalidatesTags: ['PRODUCT_TAGS'],
            }),
            updateTag: builder.mutation<
                ProductTag,
                {
                    liftOperatorId: string;
                    tagId: string;
                    payload: UpdateTagPayload;
                }
            >({
                query: ({ liftOperatorId, tagId, payload }) => ({
                    url: `${tagUrls(liftOperatorId).base}/${tagId}`,
                    method: 'PUT',
                    body: payload,
                }),
                invalidatesTags: ['PRODUCT_TAGS', 'PRODUCT_CONFIGURATION'],
            }),
            deleteTag: builder.mutation<
                void,
                { liftOperatorId: string; tagId: string; force?: boolean }
            >({
                query: ({ liftOperatorId, tagId, force }) => ({
                    url: `${tagUrls(liftOperatorId).base}/${tagId}${
                        force ? '?force=true' : ''
                    }`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['PRODUCT_TAGS', 'PRODUCT_CONFIGURATION'],
            }),
            getTagUsage: builder.query<
                TagUsage,
                { liftOperatorId: string; tagId: string }
            >({
                query: ({ liftOperatorId, tagId }) =>
                    `${tagUrls(liftOperatorId).base}/${tagId}/usage`,
            }),
            reorderTags: builder.mutation<
                ProductTag[],
                { liftOperatorId: string; orderedIds: string[] }
            >({
                query: ({ liftOperatorId, orderedIds }) => ({
                    url: `${tagUrls(liftOperatorId).base}/sort`,
                    method: 'PUT',
                    body: orderedIds,
                }),
                invalidatesTags: ['PRODUCT_TAGS', 'PRODUCT_CONFIGURATION'],
            }),
        }),
    });

export const {
    useGetTagsQuery,
    useCreateTagMutation,
    useUpdateTagMutation,
    useDeleteTagMutation,
    useGetTagUsageQuery,
    useReorderTagsMutation,
} = tagsApi;
